<!--
Alternate view of the accepted rate quote for users who do not have access to the full rate shop
-->

<template>
  <Section
    :id="id"
    :aria-label="t('rateShop.rateShop')"
    class="shipment-rate-shop-accepted-rate-summary"
  >
    <SectionHeader
      :id="`${id}-section-header`"
      :header="t('carriers.carriers')"
    >
      <template #left>
        <RygenChip :label="mileage.mileageString" />
        <InfoIcon
          v-if="!loading && !mileage.mileage"
          class="text-warning"
          icon="pi pi-exclamation-triangle"
          :tooltip="t('rateShop.warnings.mileageRequestRejected')"
        />
      </template>
    </SectionHeader>

    <FreightAllowanceViewEdit
      v-if="shouldShowFreightAllowance"
      v-model="freightAllowanceInternal"
      :disabled="!isFreightAllowanceEditable"
    />

    <SelectedRateCard v-if="fauxRate" :rate-quote="fauxRate" />
  </Section>
</template>

<script lang="ts" setup>
import axios from 'axios';
import type { Ref } from 'vue';
import { computed, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  DomainBrandingSignedDocumentReference,
  Loadable,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { useFreightAllowance, useRateShop } from '@/composables';
import {
  Carrier,
  FreightAllowance,
  RateQuote,
  RateShop,
  Shipment,
} from '@/model';

import SelectedRateCard from '@/components/carrier/SelectedRateCard.vue';
import FreightAllowanceViewEdit from '@/components/rateShop/FreightAllowanceViewEdit.vue';
import InfoIcon from '@rygen/rygen-ui/components/ui/InfoIcon.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import SectionHeader from '@rygen/rygen-ui/components/ui/SectionHeader.vue';

const props = withDefaults(
  defineProps<{
    id: string;
    loading: boolean;
    freightAllowance?: FreightAllowance;
    rateShop: Loadable<RateShop>;
    shipment: Shipment;
  }>(),
  {
    loading: false,
    freightAllowance: undefined,
  },
);
const { id, loading, freightAllowance, rateShop, shipment } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:freight-allowance', value?: FreightAllowance): void;
}>();

const {
  freightAllowanceInternal,
  isFreightAllowanceEditable,
  shouldShowFreightAllowance,
} = useFreightAllowance(freightAllowance, emit);
const { t } = useI18n();
const { acceptedRateQuote } = useRateShop(rateShop, {
  acceptedRateQuoteId: toRef(() => shipment.value.acceptedRateQuoteId),
});

const carrierLogo: Ref<DomainBrandingSignedDocumentReference | undefined> =
  ref();

const mileage = computed(() => {
  let mileage: number | null = null;
  let i18nKey: string = 'rateShop.estimatedMileageCount';

  const rateTypeHasQuotedMileage = !(
    acceptedRateQuote.value?.source === 'LANE_QUOTE' ||
    acceptedRateQuote.value?.source === 'SPOT_QUOTE'
  );

  if (shipment.value.quotedMileage && rateTypeHasQuotedMileage) {
    mileage = shipment.value.quotedMileage;
    i18nKey = 'rateShop.quotedMileageCount';
  } else if (shipment.value.estimatedMileage) {
    mileage = shipment.value.estimatedMileage;
    i18nKey = 'rateShop.estimatedMileageCount';
  }

  mileage = mileage ? Math.round(mileage) : mileage;
  const mileageString = t(i18nKey, {
    param: mileage ? t('rateShop.mileage', { count: mileage }) : '-',
  });
  return { mileage, mileageString };
});

/**
 * Faux rate quote object to be used in the SelectedRateCard component
 */
const fauxRate: Ref<RateQuote | undefined> = computed(() => {
  if (!shipment.value.acceptedRateQuoteId) {
    return;
  }

  return new RateQuote({
    carrier: new Carrier({
      id: shipment.value.carrierId,
      name: shipment.value.carrierName,
      scac: shipment.value.scac,
      type: shipment.value.carrierType,
      logo: carrierLogo.value,
    }),
    mode: shipment.value.mode,
    serviceType: shipment.value.service,
  });
});

async function fetchCarrierLogo(carrierId: number) {
  if (!authorization.hasAuthority('carrier', 'read')) {
    return;
  }

  try {
    const carrier = await api.carrier.findById(carrierId);
    carrierLogo.value = carrier.logo;
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.warn('Failed to fetch carrier logo', e);
  }
}

watch(
  () => shipment.value.carrierId,
  (carrierId) => {
    if (carrierId) {
      fetchCarrierLogo(carrierId);
    }
  },
  { immediate: true },
);
</script>

<style>
.shipment-rate-shop-accepted-rate-summary {
  --section-header-margin: 0;

  .section-data {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }

  .selected-rate-card {
    .expand-button,
    .selected-rate-card-rate,
    .selected-rate-card-rate-source,
    .selected-rate-card-rate-transit-time {
      display: none !important;
    }
  }

  .freight-allowance-view-edit {
    width: min-content;
    min-width: 175px;
  }
}
</style>
